/* Ce que les écrans fluence disent d'une mesure — au même endroit.
 *
 * Ces libellés et ce seuil vivaient en DEUX ou TROIS exemplaires, recopiés d'un
 * écran à l'autre. La dérive n'est pas théorique : la formule « sans repère
 * publié » a dû être corrigée À LA MAIN dans deux fichiers, dans deux commits
 * distincts. Le jour où l'un des deux serait oublié, deux écrans diraient une
 * chose différente du même état — et rien ne le signalerait, puisque chacun
 * reste cohérent avec lui-même.
 *
 * C'est la cinquième duplication de cette famille refermée dans la nuit du 10 au
 * 11/09 : deux méthodes voisines dont une seule portait un garde, un invariant
 * tenu sur une porte et pas l'autre, un calcul écrit deux fois dans un même
 * écran, deux normalisations d'énoncé dans deux paquets, et celle-ci. */

/* LES NIVEAUX QUE FLUENCE SAIT MESURER — écrits une fois.
 *
 * Cette liste vivait en TROIS exemplaires : l'écran de passation, l'écran de
 * l'enseignant, et l'agrégat de la vue direction. Trois copies d'une liste que
 * personne ne songe à faire diverger — jusqu'au jour où on en ajoute un.
 *
 * Et ce jour est identifié. La 6e appartient au CYCLE 3, dont le bilan de fin de
 * cycle se fait précisément en 6e (Éduscol, « Collège : les bilans »). Le jour où
 * fluence l'ouvrira, il faudra un texte de 6e dans le corpus — il n'y en a aucun
 * sur les 189 — et un repère de 6e, que le prototype ne donne pas. Avec trois
 * copies, on en aurait édité une, et deux écrans auraient proposé un niveau que
 * le troisième ignore.
 *
 * Aujourd'hui la promesse est tenue : les écrans, les contraintes de la base et
 * les seuils du domaine disent tous CP→CM2. On n'ouvre pas une porte sur une
 * pièce vide. */
export const NIVEAUX_FLUENCE = ['CP', 'CE1', 'CE2', 'CM1', 'CM2'] as const;
export type NiveauFluence = (typeof NIVEAUX_FLUENCE)[number];

export type PositionSeuil = 'sous' | 'dans' | 'au-dessus';

export interface Pastille {
  readonly label: string;
  readonly cls: string;
}

export const PASTILLE: Record<PositionSeuil, Pastille> = {
  /* Trois états, jamais une note. Un MCLM est une mesure, pas un jugement — et
     un classement d'élèves affiché en classe transformerait le chiffre en
     verdict. */
  sous: { label: 'sous le repère', cls: 'bg-naia-orange-bg text-naia-orange' },
  dans: { label: 'dans le repère', cls: 'bg-naia-green-bg text-naia-green-deep' },
  'au-dessus': { label: 'au-delà du repère', cls: 'bg-naia-info-bg text-naia-blue' },
};

/* « sans repère », et non « sans repère publié » : les écrans d'historique ne
   savent pas POURQUOI la bande manque. Deux causes existent — le niveau n'en
   publie pas (le CP), ou le texte ne se mesure pas en vitesse (25 poèmes, fables
   et scènes de théâtre, sur quatre niveaux qui ont tous un repère). On dit ce
   qu'on constate, pas ce qu'on suppose. */
export const SANS_REPERE: Pastille = { label: 'sans repère', cls: 'bg-muted text-muted-foreground' };

export const pastilleDe = (position: PositionSeuil | null): Pastille =>
  position ? PASTILLE[position] : SANS_REPERE;

/* Sous une minute, le nombre de mots par minute est EXTRAPOLÉ, et d'autant plus
   fragile que la lecture est courte. Le seuil vivait en dur dans trois écrans. */
/* RECOPIE DE `packages/domain/src/fluence/seuils-mclm.ts`, et c'est le domaine
   qui fait foi : c'est lui qui refuse la mesure. La copie n'est pas un choix —
   `apps/web` ne résout pas `@naia-app/domain`, vérifié : l'import échoue au
   typecheck. Les deux valeurs sont donc ÉPINGLÉES par un test de chaque côté,
   pour qu'aucune ne puisse bouger seule en silence. */
export const DUREE_MINIMALE_SECONDES = 5;

export const DUREE_PLEINE_SECONDES = 60;

export const estExtrapolee = (dureeSecondes: number): boolean =>
  dureeSecondes < DUREE_PLEINE_SECONDES;

/* L'ALERTE DE DÉCROCHAGE, reprise du prototype et absente jusqu'ici.
 *
 * Le prototype la calcule ainsi : les DEUX dernières lectures sous la référence.
 * Une lecture faible arrive à tout le monde — un jour de fatigue, un texte qui ne
 * parle pas. Deux d'affilée, c'est une tendance, et c'est le moment où un adulte
 * doit regarder.
 *
 * On affichait des positions ; on n'alertait jamais. La différence n'est pas
 * cosmétique : un tableau se consulte quand on y pense, une alerte se voit quand
 * on ne la cherche pas. C'est ce qui sépare un outil qu'on ouvre d'un outil qui
 * prévient. */
export const LECTURES_POUR_ALERTE = 2;

/* Les lectures SANS repère ne comptent ni pour ni contre : un texte exclu de la
   mesure de vitesse, ou un niveau sans bande, ne dit rien de l'écart à l'attendu.
   Les ignorer vaut mieux que les compter comme réussies — ce qui éteindrait
   l'alerte — ou comme échouées — ce qui la déclencherait à tort. */
export function enDecrochage(
  lecturesDuPlusRecentAuPlusAncien: readonly { position: PositionSeuil | null }[],
): boolean {
  const situables = lecturesDuPlusRecentAuPlusAncien.filter((p) => p.position !== null);
  if (situables.length < LECTURES_POUR_ALERTE) return false;
  return situables.slice(0, LECTURES_POUR_ALERTE).every((p) => p.position === 'sous');
}

/* LE POSITIONNEMENT LSU — une SUGGESTION, jamais une décision.
 *
 * Vérifié sur Éduscol, « Collège : les bilans » (février 2026) : le livret
 * scolaire unique va du CP à la troisième, et le bilan de fin de cycle positionne
 * l'élève sur une échelle de QUATRE niveaux — maîtrise insuffisante, fragile,
 * satisfaisante, très bonne maîtrise. Ce sont ces mots-là, pas des synonymes.
 *
 * La fluence n'alimente qu'UNE composante du domaine 1 du socle : comprendre et
 * s'exprimer en utilisant la langue française à l'oral. Elle ne positionne ni le
 * domaine, ni le socle — et le dire est ce qui sépare un outil d'aide d'un outil
 * qui décide à la place de l'enseignant.
 *
 * LE SEUIL DE LA MAÎTRISE FRAGILE. Nos bandes n'ont que trois positions ; le LSU
 * en demande quatre. Le prototype coupe « sous le repère » en deux à 0,75 × la
 * référence, et c'est cette coupure qui est reprise ici — faute de quoi il
 * faudrait en inventer une, ce qui serait décider de pédagogie.
 *
 * AUCUN EXPORT. Le positionnement se recopie à la main dans LSUN par
 * l'enseignant, qui reste seul juge. */
export const NIVEAUX_LSU = [
  { niveau: 1, libelle: 'Maîtrise insuffisante' },
  { niveau: 2, libelle: 'Maîtrise fragile' },
  { niveau: 3, libelle: 'Maîtrise satisfaisante' },
  { niveau: 4, libelle: 'Très bonne maîtrise' },
] as const;

export const SEUIL_FRAGILE = 0.75;

/* LES ATTENDUS NATIONAUX AFFICHÉS EN POINTILLÉ sur le graphique d'évolution —
 * demande d'un beta-testeur (Dogan, 30/09), recoupée avant implémentation.
 *
 * Dogan citait 70/90 MCLM pour le CE2 (début/fin d'année) ; le repère existant
 * porte déjà la mention « à confirmer » côté bande grise (voir
 * `fluence-eleve-detail.tsx`) et ne distingue PAS début/fin d'année — seule une
 * valeur par niveau est sourcée ici. Recoupement : `SEUILS_MCLM` dans
 * `packages/domain/src/fluence/seuils-mclm.ts` porte déjà CE1 70 et CE2 90 comme
 * `min` (« le niveau attendu »), sourcés sur le prototype Lecture &
 * Compréhension (`REP`, 09/2026) — donc les valeurs de Dogan coïncident avec une
 * source déjà citée dans ce repo, mais PAS avec le découpage qu'il décrivait
 * (CE1=70 et CE2=90, pas « début et fin de CE2 »). On réutilise ces valeurs
 * déjà sourcées, PAS celles de Dogan telles quelles, et le graphique doit dire
 * clairement qu'il s'agit d'un attendu par niveau, pas d'un repère officiel
 * début/fin d'année — l'arrêté exact reste à confirmer, comme pour la bande
 * grise. RECOPIE de `SEUILS_MCLM` (mêmes valeurs `min`) — épinglée ici ET côté
 * domaine, pour la même raison que les autres constantes de ce fichier. */
export const ATTENDUS_NATIONAUX_MCLM: Readonly<Record<NiveauFluence, number>> = Object.freeze({
  CP: 50,
  CE1: 70,
  CE2: 90,
  CM1: 110,
  CM2: 120,
});

/* LA BORNE HAUTE, demandée par Dogan comme « seuil de fin d'année » — vérifiée
 * avant d'être affichée ainsi. `SEUILS_MCLM.max` (domaine) ne représente PAS
 * une progression dans l'année : c'est 1,15 × la référence, la frontière de
 * « très bonne maîtrise » dans l'échelle à quatre positions du prototype (voir
 * le commentaire de tête de `seuils-mclm.ts`). L'appeler « fin d'année »
 * inventerait une granularité mensuelle qu'aucune source ne donne — recherche
 * faite, aucun texte Éduscol primaire trouvé à cette granularité. On affiche
 * donc la valeur réelle sous son vrai nom, pas sous celui demandé.
 * RECOPIE des mêmes `max` que `SEUILS_MCLM`, pour la même raison que
 * `ATTENDUS_NATIONAUX_MCLM` juste au-dessus. */
export const TRES_BONNE_MAITRISE_MCLM: Readonly<Record<NiveauFluence, number>> = Object.freeze({
  CP: 57,
  CE1: 80,
  CE2: 103,
  CM1: 126,
  CM2: 138,
});

export interface PositionnementLsu {
  readonly niveau: 1 | 2 | 3 | 4;
  readonly libelle: string;
}

/* La DERNIÈRE lecture situable fait foi : un positionnement de fin de cycle dit
   où l'élève en est, pas où il en était. Une lecture sans repère ne peut rien
   positionner — on rend `null` plutôt qu'un niveau inventé. */
export function positionnementLsu(
  lecturesDuPlusRecentAuPlusAncien: readonly { mclm: number; seuilMin: number | null }[],
): PositionnementLsu | null {
  const derniere = lecturesDuPlusRecentAuPlusAncien.find((p) => p.seuilMin !== null);
  if (!derniere || derniere.seuilMin === null) return null;

  const reference = derniere.seuilMin;
  const niveau = derniere.mclm >= reference * 1.15 ? 4
    : derniere.mclm >= reference ? 3
    : derniere.mclm >= reference * SEUIL_FRAGILE ? 2
    : 1;

  return { niveau, libelle: NIVEAUX_LSU[niveau - 1]!.libelle };
}

/* UN GUILLEMET SE LIT À L'ÉCRAN ET NE SE LIT PAS À VOIX HAUTE.
 *
 * Mesuré sur les 189 textes : 2 531 jetons sur 39 990 ne contiennent ni lettre
 * ni chiffre — « : » 667 fois, « « » 467, « — » 464, « » » 340. Ils étaient
 * comptés comme des mots, donc crédités dans le MCLM : 5,3 % d'inflation
 * médiane, 17 % sur les plus courts, ceux du CP et du CE1.
 *
 * La règle est recopiée de la migration `20260912060000`, qui emploie la classe
 * POSIX `[[:alnum:]]`. Les deux doivent dire la même chose : l'une a rempli
 * `mots_comptes` une fois, l'autre compte en direct ce que l'enfant a lu. Un
 * test les épingle. */
export const estMotCompte = (mot: string): boolean => /[\p{L}\p{N}]/u.test(mot);

/* LE NIVEAU DE LA CLASSE, RAPPORTÉ À CE QUE FLUENCE SAIT MESURER.
 *
 * Une classe de 4e existe, et fluence ne couvre que CP→CM2. L'application
 * laissait pourtant faire lire un élève de 4e sur un texte de CM2 sans rien
 * dire : le niveau vient du TEXTE, jamais de l'élève. Techniquement cohérent,
 * pédagogiquement absurde — et c'est ce qui a fait paraître l'écran vide de sens
 * à qui l'ouvrait depuis une classe de collège.
 *
 * ON PRÉVIENT, ON N'INTERDIT PAS. Donner un texte de CM2 à un élève de 6e en
 * difficulté est une pratique de remédiation ordinaire, et refuser une mesure
 * fait perdre une séance — le défaut qu'on a déjà réparé deux fois ici. */
export const niveauCouvert = (codeClasse: string | undefined): boolean =>
  codeClasse !== undefined
  && (NIVEAUX_FLUENCE as readonly string[]).includes(codeClasse.toUpperCase());

/* Le code de `levels` est en minuscules — « cp », « 4e » — là où fluence écrit
   « CP ». Deux écritures du même niveau, et c'est ici qu'elles se rencontrent. */
export const memeNiveau = (codeClasse: string | undefined, niveauTexte: string): boolean =>
  codeClasse !== undefined && codeClasse.toUpperCase() === niveauTexte.toUpperCase();

export type SuggestionLsu =
  | { readonly statut: 'affichee'; readonly positionnement: PositionnementLsu }
  | { readonly statut: 'texte-different' }
  | { readonly statut: 'masquee' };

export function suggestionLsu(
  lecturesDuPlusRecentAuPlusAncien: readonly { mclm: number; seuilMin: number | null; levelCode: string }[],
  codeClasse: string | undefined,
): SuggestionLsu {
  const derniere = lecturesDuPlusRecentAuPlusAncien.find((p) => p.seuilMin !== null);
  if (!derniere || !niveauCouvert(codeClasse)) return { statut: 'masquee' };
  if (!memeNiveau(codeClasse, derniere.levelCode)) return { statut: 'texte-different' };
  const positionnement = positionnementLsu([derniere]);
  return positionnement ? { statut: 'affichee', positionnement } : { statut: 'masquee' };
}
